import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const MODULE_GROUPS = ['CORE', 'FNB', 'AI', 'ADDON'];

const emptyForm = {
  module_key: '',
  module_name: '',
  module_group: 'CORE',
  description: '',
  base_price_monthly: '',
  base_price_yearly: '',
  property_flag_column: '',
  display_order: 0,
};

function SubscriptionModuleCatalog({ token }) {
  const { isMobile } = useIsMobile();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const loadModules = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/subscription/admin/modules', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load modules');
      setRows(Array.isArray(data) ? data : data.modules || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadModules(); }, [token]);

  const fmtPrice = (v) => {
    const n = Number(v);
    return isNaN(n) ? '—' : `₹${n.toFixed(2)}`;
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      module_key: row.module_key || '',
      module_name: row.module_name || '',
      module_group: row.module_group || 'CORE',
      description: row.description || '',
      base_price_monthly: row.base_price_monthly ?? '',
      base_price_yearly: row.base_price_yearly ?? '',
      property_flag_column: row.property_flag_column || '',
      display_order: row.display_order ?? 0,
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const saveModule = async () => {
    setError('');
    setSuccess('');
    if (!form.module_key.trim() || !form.module_name.trim()) {
      return setError('Module Key and Name are required.');
    }
    const url = editingId
      ? `/api/v1/subscription/admin/modules/${editingId}`
      : '/api/v1/subscription/admin/modules';
    const method = editingId ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          module_key: form.module_key.trim(),
          module_name: form.module_name.trim(),
          module_group: form.module_group,
          description: form.description.trim(),
          base_price_monthly: Number(form.base_price_monthly) || 0,
          base_price_yearly: Number(form.base_price_yearly) || 0,
          property_flag_column: form.property_flag_column.trim(),
          display_order: Number(form.display_order) || 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSuccess(editingId ? 'Module updated.' : 'Module created.');
      cancelForm();
      loadModules();
    } catch (e) {
      setError(e.message);
    }
  };

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* ── Styles ── */
  const thStyle = {
    padding: '0.6rem 0.75rem',
    textAlign: 'left',
    position: 'sticky',
    top: 0,
    background: '#f5f6fa',
    borderBottom: '2px solid var(--theme-border, #dcdde1)',
    whiteSpace: 'nowrap',
    fontSize: '0.82rem',
    fontWeight: 600,
  };
  const tdStyle = { padding: '0.55rem 0.75rem', borderBottom: '1px solid #eef2f7', fontSize: '0.85rem' };
  const btnPrimary = {
    padding: '0.5rem 1.1rem',
    background: 'var(--theme-primary, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.85rem',
  };
  const btnSecondary = {
    padding: '0.5rem 1.1rem',
    background: '#dcdde1',
    color: '#2f3640',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.85rem',
  };
  const inputStyle = {
    padding: '0.5rem 0.6rem',
    border: '1px solid var(--theme-border, #ccc)',
    borderRadius: 6,
    fontSize: '0.85rem',
    width: '100%',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontWeight: 600, fontSize: '0.8rem', marginBottom: 2, display: 'block', color: '#34495e' };

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.8rem' }}>
        <h2 style={{ color: 'var(--theme-primary-dark, #1e3a5f)', margin: 0 }}>Module Catalog</h2>
        <button style={btnPrimary} onClick={openAdd}>+ Add Module</button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {/* ── Add/Edit Panel ── */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <h3 style={{ margin: '0 0 0.8rem', color: 'var(--theme-primary-dark, #1e3a5f)' }}>
            {editingId ? 'Edit Module' : 'New Module'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Module Key</label>
              <input style={inputStyle} value={form.module_key} onChange={(e) => set('module_key', e.target.value)} placeholder="e.g. MOD_PMS" />
            </div>
            <div>
              <label style={labelStyle}>Module Name</label>
              <input style={inputStyle} value={form.module_name} onChange={(e) => set('module_name', e.target.value)} placeholder="e.g. Property Management" />
            </div>
            <div>
              <label style={labelStyle}>Group</label>
              <select style={inputStyle} value={form.module_group} onChange={(e) => set('module_group', e.target.value)}>
                {MODULE_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Short description" />
            </div>
            <div>
              <label style={labelStyle}>Monthly Price (₹)</label>
              <input style={inputStyle} type="number" min="0" step="0.01" value={form.base_price_monthly} onChange={(e) => set('base_price_monthly', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Yearly Price (₹)</label>
              <input style={inputStyle} type="number" min="0" step="0.01" value={form.base_price_yearly} onChange={(e) => set('base_price_yearly', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Property Flag Column</label>
              <input style={inputStyle} value={form.property_flag_column} onChange={(e) => set('property_flag_column', e.target.value)} placeholder="e.g. enable_pms" />
            </div>
            <div>
              <label style={labelStyle}>Display Order</label>
              <input style={inputStyle} type="number" min="0" value={form.display_order} onChange={(e) => set('display_order', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button style={btnPrimary} onClick={saveModule}>{editingId ? 'Update' : 'Create'}</button>
            <button style={btnSecondary} onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading && <div style={{ color: '#636e72', marginBottom: '0.8rem' }}>Loading...</div>}

      <div className="card" style={{ overflowX: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Module Key</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Group</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Monthly Price</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Yearly Price</th>
              <th style={thStyle}>Flag Column</th>
              <th style={thStyle}>Active</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid #eef2f7' }}>
                <td style={tdStyle}><code>{r.module_key}</code></td>
                <td style={tdStyle}>{r.module_name}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, background: r.module_group === 'CORE' ? '#dff9e8' : r.module_group === 'AI' ? '#e8e0ff' : r.module_group === 'FNB' ? '#fff4d6' : '#e0f0ff', color: '#333' }}>
                    {r.module_group}
                  </span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmtPrice(r.base_price_monthly)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmtPrice(r.base_price_yearly)}</td>
                <td style={tdStyle}><code>{r.property_flag_column || '—'}</code></td>
                <td style={tdStyle}>
                  <span style={{ color: r.is_active !== false ? '#1f8f4d' : '#c23616', fontWeight: 600 }}>
                    {r.is_active !== false ? 'Yes' : 'No'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <button style={{ ...btnSecondary, padding: '0.3rem 0.7rem', fontSize: '0.8rem' }} onClick={() => openEdit(r)}>Edit</button>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={8} style={{ padding: '1rem', color: '#7f8c8d', textAlign: 'center' }}>No modules found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SubscriptionModuleCatalog;
